import React, { useMemo, useState } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
    defaultDropAnimationSideEffects,
    DragStartEvent,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    horizontalListSortingStrategy,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { createPortal } from 'react-dom';

interface SortableListProps<T> {
    items: T[];
    onReorder: (items: T[]) => void;
    renderItem: (item: T, isDragging?: boolean) => React.ReactNode;
    keyExtractor: (item: T) => string;
    direction?: 'row' | 'column';
    className?: string;
}

export function SortableList<T>({
    items,
    onReorder,
    renderItem,
    keyExtractor,
    direction = 'row',
    className,
}: SortableListProps<T>) {
    const [activeId, setActiveId] = useState<string | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Require slight movement to start drag, preventing accidental clicks
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const strategy = direction === 'row' ? horizontalListSortingStrategy : verticalListSortingStrategy;

    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(event.active.id as string);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = items.findIndex((item) => keyExtractor(item) === active.id);
            const newIndex = items.findIndex((item) => keyExtractor(item) === over.id);
            onReorder(arrayMove(items, oldIndex, newIndex));
        }

        setActiveId(null);
    };

    const activeItem = useMemo(
        () => items.find((item) => keyExtractor(item) === activeId),
        [activeId, items, keyExtractor]
    );

    const dropAnimation = {
        sideEffects: defaultDropAnimationSideEffects({
            styles: {
                active: {
                    opacity: '0.5',
                },
            },
        }),
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <SortableContext items={items.map(keyExtractor)} strategy={strategy}>
                <div className={className}>
                    {items.map((item) => (
                        <SortableItem
                            key={keyExtractor(item)}
                            id={keyExtractor(item)}
                        >
                            {renderItem(item)}
                        </SortableItem>
                    ))}
                </div>
            </SortableContext>

            {createPortal(
                <DragOverlay dropAnimation={dropAnimation}>
                    {activeItem ? renderItem(activeItem, true) : null}
                </DragOverlay>,
                document.body
            )}
        </DndContext>
    );
}

interface SortableItemProps {
    id: string;
    children: React.ReactNode;
}

function SortableItem({ id, children }: SortableItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.3 : 1,
        position: 'relative' as const,
        zIndex: isDragging ? 999 : 'auto',
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            {children}
        </div>
    );
}
